import React from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { Clock, ArrowLeft, ChevronRight } from 'lucide-react';
import { Helmet } from 'react-helmet';
import { getBlogBySlug, blogs } from '../../data/blogs';
import BlogLayout from './BlogLayout';

/* ─── Block renderer ───────────────────────────────────────────────── */

const calloutStyles = {
  tip: 'bg-teal-50 border-l-4 border-teal-500 text-teal-800',
  highlight: 'bg-yellow-50 border-l-4 border-yellow-400 text-yellow-900',
  warning: 'bg-red-50 border-l-4 border-red-400 text-red-800',
};

const BlogBlock = ({ block, onBookingOpen }) => {
  switch (block.type) {
    case 'heading':
      return (
        <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4 leading-snug">
          {block.text}
        </h2>
      );
    case 'heading2':
      return (
        <h3 className="text-xl font-semibold text-gray-800 mt-7 mb-3 leading-snug">
          {block.text}
        </h3>
      );
    case 'paragraph':
      return (
        <p className="text-gray-700 leading-relaxed mb-5 text-base">
          {block.text}
        </p>
      );
    case 'list':
      return block.style === 'ordered' ? (
        <ol className="list-decimal list-outside ml-6 mb-6 space-y-2 text-gray-700">
          {block.items.map((item, i) => (
            <li key={i} className="leading-relaxed pl-1">{item}</li>
          ))}
        </ol>
      ) : (
        <ul className="list-disc list-outside ml-6 mb-6 space-y-2 text-gray-700">
          {block.items.map((item, i) => (
            <li key={i} className="leading-relaxed pl-1">{item}</li>
          ))}
        </ul>
      );
    case 'callout':
      return (
        <div className={`rounded-lg p-5 mb-6 ${calloutStyles[block.variant] || calloutStyles.tip}`}>
          <p className="font-medium leading-relaxed">{block.text}</p>
        </div>
      );
    case 'cta':
      return (
        <div className="bg-teal-700 rounded-xl p-8 my-10 text-center">
          <h3 className="text-xl font-bold text-white mb-2">{block.heading}</h3>
          <p className="text-yellow-300 mb-6 text-sm">{block.subtext}</p>
          <button
            onClick={onBookingOpen}
            className="px-8 py-3 bg-yellow-500 text-black font-bold rounded-md hover:bg-yellow-400 transition-colors"
          >
            {block.buttonText}
          </button>
        </div>
      );
    default:
      return null;
  }
};

/* ─── Article schema helper ────────────────────────────────────────── */

const ArticleSchema = ({ post }) => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.card.title,
    datePublished: post.publishedAt,
    dateModified: post.publishedAt,
    url: post.seo.canonicalUrl,
    description: post.seo.description,
    author: {
      '@type': 'Person',
      name: 'Subhransu Dutta',
      jobTitle: 'Career Counselling Expert',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Nirvana Career Counselling',
      url: 'https://nirvanacareercounselling.com',
      logo: {
        '@type': 'ImageObject',
        url: 'https://www.edumilestones.com/crm/images/logo.png',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': post.seo.canonicalUrl,
    },
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
};

/* ─── Related posts ────────────────────────────────────────────────── */

const RelatedPosts = ({ currentSlug }) => {
  const related = blogs.filter((b) => b.slug !== currentSlug).slice(0, 3);
  return (
    <aside className="mt-12 pt-10 border-t border-gray-200">
      <h2 className="text-xl font-bold text-gray-900 mb-6">More Articles</h2>
      <div className="grid sm:grid-cols-3 gap-6">
        {related.map((post) => (
          <Link
            key={post.slug}
            to={`/blog/${post.slug}`}
            className="group block bg-gray-50 rounded-lg p-4 hover:bg-teal-50 transition-colors border border-gray-100 hover:border-teal-200"
          >
            <span className="text-xs font-semibold text-teal-600 mb-2 block">
              {post.card.category}
            </span>
            <h3 className="text-sm font-semibold text-gray-800 group-hover:text-teal-700 transition-colors leading-snug mb-2">
              {post.card.title}
            </h3>
            <span className="inline-flex items-center text-teal-600 text-xs font-medium">
              Read <ChevronRight className="h-3 w-3 ml-0.5" />
            </span>
          </Link>
        ))}
      </div>
    </aside>
  );
};

/* ─── Post content wrapper ─────────────────────────────────────────── */

const BlogPostContent = ({ post, onBookingOpen }) => {
  const formattedDate = new Date(post.publishedAt).toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
      {/* Back link */}
      <Link
        to="/blog"
        className="inline-flex items-center text-teal-600 text-sm font-medium hover:text-teal-800 mb-8 transition-colors"
      >
        <ArrowLeft className="h-4 w-4 mr-1" />
        Back to Blog
      </Link>

      {/* Article header */}
      <header className="mb-10">
        <div className="flex items-center space-x-3 mb-4">
          <span className="bg-teal-100 text-teal-700 text-xs font-semibold px-3 py-1 rounded-full">
            {post.card.category}
          </span>
          <span className="flex items-center text-gray-400 text-xs">
            <Clock className="h-3 w-3 mr-1" />
            {post.readingTimeMinutes} min read
          </span>
          <span className="text-gray-400 text-xs">{formattedDate}</span>
        </div>
        <h1 className="text-3xl md:text-4xl font-black text-gray-900 leading-tight mb-4">
          {post.card.title}
        </h1>
        <p className="text-gray-600 text-lg leading-relaxed">{post.card.excerpt}</p>
        <div className="mt-5 pt-5 border-t border-gray-200 flex items-center space-x-3">
          <div>
            <p className="text-sm font-semibold text-gray-800">Subhransu Dutta</p>
            <p className="text-xs text-gray-500">Career Counselling Expert, 30+ Years Experience</p>
          </div>
        </div>
      </header>

      {/* Article body */}
      <article>
        {post.body.map((block, i) => (
          <BlogBlock key={i} block={block} onBookingOpen={onBookingOpen} />
        ))}
      </article>

      {/* Related posts */}
      <RelatedPosts currentSlug={post.slug} />
    </div>
  );
};

/* ─── Main export ──────────────────────────────────────────────────── */

const BlogPost = () => {
  const { slug } = useParams();
  const post = getBlogBySlug(slug);

  if (!post) return <Navigate to="/blog" replace />;

  return (
    <>
      <Helmet>
        <title>{post.seo.title}</title>
        <meta name="description" content={post.seo.description} />
        <meta name="keywords" content={post.seo.keywords} />
        <link rel="canonical" href={post.seo.canonicalUrl} />
        <meta property="og:title" content={post.seo.title} />
        <meta property="og:description" content={post.seo.description} />
        <meta property="og:url" content={post.seo.canonicalUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={post.seo.ogImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.seo.title} />
        <meta name="twitter:description" content={post.seo.description} />
      </Helmet>
      <ArticleSchema post={post} />
      <BlogLayout>
        <BlogPostContent post={post} />
      </BlogLayout>
    </>
  );
};

export default BlogPost;
